import { useState } from 'react'
import { Link, Outlet, useLocation } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AppShell, PageBody } from '@/components/AppShell'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { adminApi } from '@/lib/api'
import { cn } from '@/lib/utils'

const TABS = [
  { to: '/admin', label: 'Zbiory', exact: true },
  { to: '/admin/reviewers', label: 'Recenzenci', exact: false },
  { to: '/admin/links', label: 'Linki', exact: false },
  { to: '/admin/feedback', label: 'Feedback', exact: false },
] as const

/** One password, one cookie. Everything under here needs it. */
export function AdminLayout() {
  const client = useQueryClient()
  const location = useLocation()
  const session = useQuery({
    queryKey: ['admin-session'],
    queryFn: () => adminApi.session(),
    retry: false,
  })

  if (session.isLoading) {
    return (
      <AppShell>
        <PageBody>
          <Skeleton className="h-40 w-full rounded-xl" />
        </PageBody>
      </AppShell>
    )
  }

  if (session.isError) {
    return <LoginScreen onDone={() => void client.invalidateQueries()} />
  }

  return (
    <AppShell
      right={
        <Button
          variant="ghost"
          size="sm"
          onClick={async () => {
            await adminApi.logout()
            await client.invalidateQueries()
          }}
        >
          Wyloguj
        </Button>
      }
    >
      <div className="border-b bg-card">
        <nav className="mx-auto flex w-full max-w-[1460px] gap-1 px-5 sm:px-7">
          {TABS.map((tab) => {
            const active = tab.exact
              ? location.pathname === tab.to
              : location.pathname.startsWith(tab.to)
            return (
              <Link
                key={tab.to}
                to={tab.to}
                className={cn(
                  'border-b-2 px-3 py-3 text-sm font-medium transition-colors',
                  active
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                {tab.label}
              </Link>
            )
          })}
        </nav>
      </div>
      <Outlet />
    </AppShell>
  )
}

function LoginScreen({ onDone }: { onDone: () => void }) {
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const login = useMutation({
    mutationFn: () => adminApi.login(password),
    onSuccess: onDone,
    onError: () => setError('Błędne hasło.'),
  })

  return (
    <AppShell>
      <PageBody className="max-w-[420px] pt-16">
        <Card>
          <CardHeader>
            <CardTitle>Zaloguj się</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={(event) => {
                event.preventDefault()
                setError(null)
                login.mutate()
              }}
            >
              <FieldGroup>
                <Field data-invalid={error ? true : undefined}>
                  <FieldLabel htmlFor="password">Hasło</FieldLabel>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    aria-invalid={error ? true : undefined}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                  {error ? <FieldError>{error}</FieldError> : null}
                </Field>
                <Button type="submit" disabled={login.isPending}>
                  {login.isPending ? 'Sprawdzanie…' : 'Wejdź'}
                </Button>
              </FieldGroup>
            </form>
          </CardContent>
        </Card>
      </PageBody>
    </AppShell>
  )
}
